

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {

  static targets = [
    "discountCodeSelect",
    "discountCodeButton",
    "discountCodeMessage",
    "discountCodeSpinner",
    "discountCodeId",
    "couponTab",
    "amountTab",
    "couponTabContent",
    "amountTabContent",
    "oneOffDiscountAmount",
    "oneOffDiscountType",
    "oneOffDiscountButton",
    "oneOffDiscountMessage",
    "oneOffDiscountCodeId",
    "isOneOffDiscount"
  ];

  connect() {
    this.updateDiscountCodeButtonState();

    // Listen for package selection changes
    document.addEventListener('change', (event) => {
      if (event.target.name === 'selected_package') {
        this.updateDiscountCodeButtonState();
      }
    });
  }

  onDiscountCodeChange(event) {
    this.updateDiscountCodeButtonState();
  }

  updateDiscountCodeButtonState() {
    if (!this.hasDiscountCodeButtonTarget || !this.hasDiscountCodeSelectTarget) {
      return;
    }

    const selectedPackage = document.querySelector('input[name="selected_package"]:checked');
    const hasPackageSelected = selectedPackage !== null;
    const hasCodeSelected = this.discountCodeSelectTarget.value !== '';

    this.discountCodeButtonTarget.disabled = !(hasPackageSelected && hasCodeSelected);
  }

  async validateDiscountCode(event) {
    event.preventDefault();

    if (!this.hasDiscountCodeSelectTarget || !this.hasDiscountCodeMessageTarget) {
      return;
    }

    const code = this.discountCodeSelectTarget.value;
    if (!code) {
      return;
    }

    const selectedPackage = document.querySelector('input[name="selected_package"]:checked');
    if (!selectedPackage) {
      this.discountCodeMessageTarget.innerHTML = '<span class="u-textRed">Please select a package before applying discount code</span>';
      this.discountCodeMessageTarget.style.display = 'block';
      this.discountCodeMessageTarget.className = 'mt1';
      return;
    }

    // Show spinner
    if (this.hasDiscountCodeSpinnerTarget) {
      this.discountCodeSpinnerTarget.style.display = 'block';
    }
    this.discountCodeMessageTarget.style.display = 'none';

    // Get school token
    const schoolTokenElement = document.getElementById('us-school-token');
    const schoolToken = schoolTokenElement ? schoolTokenElement.textContent.trim() : '';

    try {
      const params = new URLSearchParams({
        code: code,
        school_token: schoolToken,
        package_id: selectedPackage.value
      });

      const response = await fetch(`/us/discount-code/validate?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        credentials: 'same-origin'
      });

      const data = await response.json();

      // Hide spinner
      if (this.hasDiscountCodeSpinnerTarget) {
        this.discountCodeSpinnerTarget.style.display = 'none';
      }

      if (response.ok && data.valid) {
        // Show success message with discount details
        const discountText = data.formatted_discount || `${data.discount_type === 'Percent' ? data.discount_amount + '%' : '$' + data.discount_amount} off`;
        this.discountCodeMessageTarget.innerHTML = `<span class="u-textSuccessMid">✓ Discount applied: ${discountText}</span>`;
        this.discountCodeMessageTarget.style.display = 'block';
        this.discountCodeMessageTarget.className = 'mt1';

        // Store the discount code ID
        if (data.discount_code_id && this.hasDiscountCodeIdTarget) {
          this.discountCodeIdTarget.value = data.discount_code_id;
        }
      } else {
        // Show error message
        this.discountCodeMessageTarget.innerHTML = `<span class="u-textRed">${data.error || 'Invalid discount code'}</span>`;
        this.discountCodeMessageTarget.style.display = 'block';
        this.discountCodeMessageTarget.className = 'mt1';
        if (this.hasDiscountCodeIdTarget) {
          this.discountCodeIdTarget.value = '';
        }
        
        // Re-enable deposit button when discount is invalid
        this.enableDepositButton();
      }
    } catch (error) {
      // Hide spinner
      if (this.hasDiscountCodeSpinnerTarget) {
        this.discountCodeSpinnerTarget.style.display = 'none';
      }
      this.discountCodeMessageTarget.innerHTML = '<span class="u-textRed">Error validating code. Please try again.</span>';
      this.discountCodeMessageTarget.style.display = 'block';
      this.discountCodeMessageTarget.className = 'mt1';
      if (this.hasDiscountCodeIdTarget) {
        this.discountCodeIdTarget.value = '';
      }
      
      // Re-enable deposit button on error
      this.enableDepositButton();
    }
  }

  switchToCouponTab(event) {
    event.preventDefault();
    this.couponTabTarget.classList.add('is-active');
    this.amountTabTarget.classList.remove('is-active');
    this.couponTabContentTarget.style.display = 'block';
    this.amountTabContentTarget.style.display = 'none';

    // Clear one-off discount fields
    if (this.hasOneOffDiscountAmountTarget) {
      this.oneOffDiscountAmountTarget.value = '';
    }
    if (this.hasOneOffDiscountMessageTarget) {
      this.oneOffDiscountMessageTarget.style.display = 'none';
    }
    if (this.hasIsOneOffDiscountTarget) {
      this.isOneOffDiscountTarget.value = 'false';
    }
    
    // Re-enable deposit button when clearing one-off discount
    this.enableDepositButton();
  }

  switchToAmountTab(event) {
    event.preventDefault();
    this.amountTabTarget.classList.add('is-active');
    this.couponTabTarget.classList.remove('is-active');
    this.amountTabContentTarget.style.display = 'block';
    this.couponTabContentTarget.style.display = 'none';

    // Clear coupon discount fields
    if (this.hasDiscountCodeSelectTarget) {
      this.discountCodeSelectTarget.value = '';
    }
    if (this.hasDiscountCodeMessageTarget) {
      this.discountCodeMessageTarget.style.display = 'none';
    }
    if (this.hasDiscountCodeIdTarget) {
      this.discountCodeIdTarget.value = '';
    }

    this.updateOneOffDiscountButtonState();
    
    // Re-enable deposit button when switching away from coupon
    this.enableDepositButton();
  }

  onOneOffDiscountChange(event) {
    this.updateOneOffDiscountButtonState();
  }

  updateMaxAttribute(event) {
    if (!this.hasOneOffDiscountAmountTarget || !this.hasOneOffDiscountTypeTarget) {
      return;
    }

    const type = this.oneOffDiscountTypeTarget.value;
    if (type === 'Percent') {
      this.oneOffDiscountAmountTarget.setAttribute('max', '99');
    } else {
      this.oneOffDiscountAmountTarget.removeAttribute('max');
    }
  }

  updateOneOffDiscountButtonState() {
    if (!this.hasOneOffDiscountButtonTarget || !this.hasOneOffDiscountAmountTarget) {
      return;
    }

    const selectedPackage = document.querySelector('input[name="selected_package"]:checked');
    const hasPackageSelected = selectedPackage !== null;
    const hasAmount = this.oneOffDiscountAmountTarget.value !== '' && parseFloat(this.oneOffDiscountAmountTarget.value) > 0;

    this.oneOffDiscountButtonTarget.disabled = !(hasPackageSelected && hasAmount);
  }

  async applyOneOffDiscount(event) {
    event.preventDefault();

    if (!this.hasOneOffDiscountAmountTarget || !this.hasOneOffDiscountTypeTarget) {
      return;
    }

    const amount = parseFloat(this.oneOffDiscountAmountTarget.value);
    const type = this.oneOffDiscountTypeTarget.value;

    if (!amount || amount <= 0) {
      if (this.hasOneOffDiscountMessageTarget) {
        this.oneOffDiscountMessageTarget.innerHTML = '<span class="u-textRed">Discount amount must be greater than 0</span>';
        this.oneOffDiscountMessageTarget.style.display = 'block';
        this.oneOffDiscountMessageTarget.className = 'mt1';
      }
      return;
    }

    // Validate percentage discounts max 99%
    if (type === 'Percent' && amount > 99) {
      if (this.hasOneOffDiscountMessageTarget) {
        this.oneOffDiscountMessageTarget.innerHTML = '<span class="u-textRed">Percentage discounts cannot exceed 99%</span>';
        this.oneOffDiscountMessageTarget.style.display = 'block';
        this.oneOffDiscountMessageTarget.className = 'mt1';
      }
      return;
    }

    const selectedPackage = document.querySelector('input[name="selected_package"]:checked');
    if (!selectedPackage) {
      if (this.hasOneOffDiscountMessageTarget) {
        this.oneOffDiscountMessageTarget.innerHTML = '<span class="u-textRed">Please select a package before applying discount</span>';
        this.oneOffDiscountMessageTarget.style.display = 'block';
        this.oneOffDiscountMessageTarget.className = 'mt1';
      }
      return;
    }

    // Get school token
    const schoolTokenElement = document.getElementById('us-school-token');
    const schoolToken = schoolTokenElement ? schoolTokenElement.textContent.trim() : '';

    try {
      const response = await fetch('/schools/setup/packages/discounts/create_one_off', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          school_token: schoolToken,
          discount_amount: amount,
          discount_type: type,
          package_id: selectedPackage.value
        })
      });

      const data = await response.json();

      if (response.ok && data.discount_code_id) {
        // Show success message
        const discountText = type === 'Percent' ? `${amount}% off` : `$${amount} off`;
        if (this.hasOneOffDiscountMessageTarget) {
          this.oneOffDiscountMessageTarget.innerHTML = `<span class="u-textGreen">✓ One-off discount will be Applied: ${discountText}</span>`;
          this.oneOffDiscountMessageTarget.style.display = 'block';
          this.oneOffDiscountMessageTarget.className = 'mt1';
        }

        // Store the discount code ID
        if (this.hasOneOffDiscountCodeIdTarget) {
          this.oneOffDiscountCodeIdTarget.value = data.discount_code_id;
        }
        if (this.hasIsOneOffDiscountTarget) {
          this.isOneOffDiscountTarget.value = 'true';
        }
        // Also set the regular discount_code_id for form submission
        if (this.hasDiscountCodeIdTarget) {
          this.discountCodeIdTarget.value = data.discount_code_id;
        }
      } else {
        // Show error message
        if (this.hasOneOffDiscountMessageTarget) {
          this.oneOffDiscountMessageTarget.innerHTML = `<span class="u-textRed">${data.error || 'Error creating one-off discount'}</span>`;
          this.oneOffDiscountMessageTarget.style.display = 'block';
          this.oneOffDiscountMessageTarget.className = 'mt1';
        }
      }
    } catch (error) {
      console.error('Error creating one-off discount:', error);
      if (this.hasOneOffDiscountMessageTarget) {
        this.oneOffDiscountMessageTarget.innerHTML = '<span class="u-textRed">Error creating discount. Please try again.</span>';
        this.oneOffDiscountMessageTarget.style.display = 'block';
        this.oneOffDiscountMessageTarget.className = 'mt1';
      }
    }
  }

  completedForm(event) {

    this.element.dispatchEvent(new CustomEvent('fullyComplete', {
      detail: { formName: 'driving-school-admin-booking-form-v2-payment-details',
      lessonDetails: {} }
    }));

  }

  disableDepositButton() {
    // Try multiple selectors to find the deposit button (admin flow uses "free-button")
    const depositButton = document.getElementById("pay-deposit-button") || 
                         document.getElementById("free-button") ||
                         document.querySelector('[data-action*="payDepositPressed"]');
    if (depositButton) {
      depositButton.disabled = true;
    }
    
    // Show the deposit unavailable message
    const depositMessage = document.getElementById('deposit-unavailable-message');
    if (depositMessage) {
      depositMessage.style.display = 'block';
    }
  }

  enableDepositButton() {
    // Try multiple selectors to find the deposit button (admin flow uses "free-button")
    const depositButton = document.getElementById("pay-deposit-button") || 
                         document.getElementById("free-button") ||
                         document.querySelector('[data-action*="payDepositPressed"]');
    if (depositButton) {
      depositButton.disabled = false;
    }
    
    // Hide the deposit unavailable message
    const depositMessage = document.getElementById('deposit-unavailable-message');
    if (depositMessage) {
      depositMessage.style.display = 'none';
    }
  }

}